import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { fromEvent, merge, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { currentUser } from '../../../../../core/auth';
import { AppState } from '../../../../../core/reducers';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { Category, CategoryService } from '../../../../../core/lists';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import { Form, FormFeatureLinks } from '../../models';
import { FormSystem } from '../../enums';
import { FormService } from '../../services';
import { FormDataSource } from '../../data-sources';
import { RequestFormPage, UpdateFormStatusOnServer } from '../../actions';
import { selectFormActionLoading, selectFormError } from '../../selectors';
import { CreateFormComponent } from '../create-form/create-form.component';
import { CreateFormSynonymLinkComponent } from '../create-form-synonym-link/create-form-synonym-link.component';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-form-list',
  templateUrl: './form-list.component.html',
  styleUrls: ['./form-list.component.scss'],
})
export class FormListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  isSuperAdmin: boolean;
  formSystems: FormSystem[] = Object.values(FormSystem);
  dataSource: FormDataSource;
  displayedCols: string[];
  searchBarCols: string[];
  showSearchBar = false;
  isActiveTracker = 0;
  getTeamAutocomplete = this.categoryService.getAllCategories.bind(this.categoryService);
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  @ViewChild('searchVersionInput') searchVersionInput: ElementRef;
  @ViewChild('searchSystemSelect') searchSystemSelect: MatSelect;
  @ViewChild('searchTeamAutocomplete') searchTeamAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchIsActiveSelect') searchIsActiveSelect: MatSelect;

  constructor(
    public stringUtils: StringUtilsService,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translate: TranslateService,
    private titleCasePipe: TitleCasePipe,
    private layoutUtils: LayoutUtilsService,
    private permissionsService: NgxPermissionsService,
    private categoryService: CategoryService,
    private formService: FormService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store.select(currentUser).subscribe((user) => {
        this.isSuperAdmin = user.userType === 'Super Admin';
        this.displayedCols = [
          'id',
          'name',
          ...(this.isSuperAdmin ? [] : ['teams', 'system']),
          'version',
          'isActive',
          'actions',
        ];
        this.searchBarCols = [
          'searchId',
          'searchName',
          ...(this.isSuperAdmin ? [] : ['searchTeam', 'searchSystem']),
          'searchVersion',
          'searchIsActive',
          'searchActions',
        ];
      }),
    );

    this.dataSource = new FormDataSource(this.store, this.paginator);

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadFormList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadFormList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadFormList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFilterConfig() {
    const filter: any = {};

    if (this.searchIsActiveSelect?.value === undefined) this.searchIsActiveSelect.value = 'true';

    filter.isActive = this.searchIsActiveSelect?.value;

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.name = this.searchNameInput?.nativeElement?.value;
      filter.version = this.searchVersionInput?.nativeElement?.value;
      filter.system = this.searchSystemSelect?.value;
      filter.teamId = this.searchTeamAutocomplete?.value?.id;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }
    return filter;
  }

  loadFormList(): void {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestFormPage({ query }));
  }

  displayMessage(message: string, messageType: MessageType): void {
    this.store
      .pipe(
        select(selectFormActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectFormError), take(1)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
            setTimeout(() => this.loadFormList(), 500);
          });
        }),
      )
      .subscribe();
  }

  formatTeamOption(team: Category): string {
    return team?.name || '';
  }

  addForm(event: Event, useGlobal?: boolean): void {
    event.stopPropagation();

    const form = new Form();
    this.updateForm(null, form, useGlobal);
  }

  updateForm(event: Event, form: Form, useGlobal?: boolean): void {
    event?.stopPropagation();

    const message = this.translate.instant(
      `FORMS_TEMPLATES_MODULE.FORM.${form.id ? 'UPDATE_MESSAGE' : 'CREATE_MESSAGE'}`,
    );
    const messageType = form.id ? MessageType.Update : MessageType.Create;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = { form, isSuperAdmin: this.isSuperAdmin, useGlobal, viewMode: false };

    const dialogRef = this.dialog.open(CreateFormComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(message, messageType);
      });
  }

  canViewForm(): boolean {
    return !!this.permissionsService.getPermission('canViewForm');
  }

  viewForm(event: Event, form: Form): void {
    event.stopPropagation();

    if (!this.canViewForm()) return;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = { form, isSuperAdmin: this.isSuperAdmin, viewMode: true };

    this.dialog.open(CreateFormComponent, dialogConfig);
  }

  async toggleFormStatus(event: Event, form: Form): Promise<void> {
    event.stopPropagation();

    const title = this.translate.instant('FORMS_TEMPLATES_MODULE.FORM.UPDATE_STATUS.TITLE');
    const waitDescription = this.translate.instant(
      'FORMS_TEMPLATES_MODULE.FORM.UPDATE_STATUS.WAIT_DESCRIPTION',
    );
    const message = this.translate.instant('FORMS_TEMPLATES_MODULE.FORM.UPDATE_STATUS.MESSAGE');
    const messageType = MessageType.Update;

    let links: FormFeatureLinks;

    let description = `
      ${
        !this.isSuperAdmin && form.is_active && form.has_mappings
          ? `<div class="font-size-lg font-weight-boldest mb-10">
        <span class="text-danger">WARNING:</span>
        <span>This form fields are already mapped to a template fields.</span>
      </div>`
          : ''
      }
      <div>
        <span class="font-weight-bolder">Are you sure you want to change the status of this form?</span>
        ${
          !this.isSuperAdmin && form.is_active && form.has_mappings
            ? '<span>By hitting Confirm, the form-template mapping will be lost.</span>'
            : ''
        }
      </div>
    `;

    if (!this.isSuperAdmin && form.is_active)
      links = await this.formService
        .getFormFeatureLinks(form.id)
        .pipe(map((res: { data: any }) => res?.data))
        .toPromise();

    const feature = links?.task_templates.length
      ? 'TASK_TEMPLATE'
      : links?.document_categories.length
      ? 'DOC_CATEGORY'
      : null;

    if (feature)
      description = `
      <div class="font-size-lg font-weight-boldest mb-5">
        <span class="text-danger">WARNING:</span>
        <span>This form is already in use in the ${
          feature === 'TASK_TEMPLATE' ? 'task templates' : 'document categories'
        } mentioned below.</span>
      </div>
      <div class="font-weight-bold text-dark-50 mb-10">
        ${links[feature === 'TASK_TEMPLATE' ? 'task_templates' : 'document_categories']
          .map((l) => l.name)
          .join(', ')}.
      </div>
      <div>
        <span class="font-weight-bolder">To deactivate this form,</span>
        <span>please remove it from the mentioned ${
          feature === 'TASK_TEMPLATE' ? 'task templates' : 'document categories'
        }.</span>
      </div>
    `;

    const dialogRef = this.layoutUtils.confirmAction(
      title,
      description,
      waitDescription,
      !!feature,
    );

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        form = { ...form, is_active: !form.is_active };

        this.store.dispatch(new UpdateFormStatusOnServer({ form }));
        this.displayMessage(message, messageType);
      });
  }

  linkFormFieldsToSynonyms(event: Event, form: Form): void {
    event.stopPropagation();

    const message = this.translate.instant('FORMS_TEMPLATES_MODULE.FORM.LINK_MESSAGE');
    const messageType = MessageType.Update;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '1000px';
    dialogConfig.data = { ...form, teams: (form.teams as Category[])?.map((team) => team.id) };

    const dialogRef = this.dialog.open(CreateFormSynonymLinkComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(message, messageType);
      });
  }

  reloadFormList(): void {
    this.paginator.pageIndex = 0;

    this.loadFormList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    this.searchVersionInput.nativeElement.value = '';

    if (this.searchSystemSelect) this.searchSystemSelect.value = '';
    this.searchTeamAutocomplete?.setValue(null);
    this.searchTeamAutocomplete?.updateValueAndValidity();
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadFormList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadFormList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadFormList();
  }

  getStatusClass(status: string): string {
    return status === 'true' ? 'success' : status === 'false' ? 'danger' : 'warning';
  }

  getStatusLabel(status: string): string {
    const translateLabel = `GLOBAL.${
      status === 'true' ? 'ACTIVE' : status === 'false' ? 'INACTIVE' : 'ALL'
    }`;

    return this.translate.instant(translateLabel);
  }

  toggleIsActive(event: Event): void {
    event.stopPropagation();

    this.isActiveTracker = (this.isActiveTracker + 1) % 3;

    switch (this.isActiveTracker) {
      case 0:
        this.searchIsActiveSelect.value = 'true';
        break;
      case 1:
        this.searchIsActiveSelect.value = 'false';
        break;
      case 2:
        this.searchIsActiveSelect.value = '';
        break;
    }

    this.reloadFormList();
  }

  handleIsActiveChange(): void {
    switch (this.searchIsActiveSelect.value) {
      case 'true':
        this.isActiveTracker = 0;
        break;
      case 'false':
        this.isActiveTracker = 1;
        break;
      case '':
        this.isActiveTracker = 2;
        break;
    }
  }
}
